import { useEffect } from 'react';
import {
  SESSION_INACTIVITY_TIMEOUT_MS,
  clearSessionActivity,
  isSessionInactive,
  markSessionActivity,
} from '../../helpers/session_activity';
import {
  attemptSilentRelogin,
  clearStoredCredentials,
  getAccessTokenFromStorage,
  isAccessTokenExpired,
} from '../../helpers/auth_relogin';
import { clearAuthUser, getAuthUserString } from '../../helpers/auth_storage';

const ACTIVITY_UPDATE_THROTTLE_MS = 30 * 1000;
const CHECK_INTERVAL_MS = 30 * 1000;

const SessionActivityManager = () => {
  useEffect(() => {
    let lastMarkedAt = 0;
    let refreshing = false;

    const hasAuthSession = () => !!getAuthUserString();

    const performLogout = () => {
      clearAuthUser();
      clearStoredCredentials();
      clearSessionActivity();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    };

    const updateActivity = () => {
      if (!hasAuthSession()) return;
      const now = Date.now();
      if (now - lastMarkedAt < ACTIVITY_UPDATE_THROTTLE_MS) return;
      lastMarkedAt = now;
      markSessionActivity();
    };

    const ensureValidSession = async () => {
      if (!hasAuthSession() || refreshing) return;

      if (isSessionInactive(SESSION_INACTIVITY_TIMEOUT_MS)) {
        performLogout();
        return;
      }

      const token = getAccessTokenFromStorage();
      if (!isAccessTokenExpired(token)) return;

      refreshing = true;
      try {
        const ok = await attemptSilentRelogin();
        if (!ok) {
          performLogout();
        }
      } finally {
        refreshing = false;
      }
    };

    if (hasAuthSession()) {
      if (isSessionInactive(SESSION_INACTIVITY_TIMEOUT_MS)) {
        performLogout();
        return;
      }
      markSessionActivity();
      lastMarkedAt = Date.now();
      void ensureValidSession();
    }

    const intervalId = window.setInterval(() => {
      void ensureValidSession();
    }, CHECK_INTERVAL_MS);

    const events: Array<keyof WindowEventMap> = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    const onEvent = () => {
      if (document.visibilityState === 'hidden') return;
      updateActivity();
      void ensureValidSession();
    };

    events.forEach((eventName) =>
      window.addEventListener(eventName, onEvent, { passive: true })
    );
    document.addEventListener('visibilitychange', onEvent);

    return () => {
      window.clearInterval(intervalId);
      events.forEach((eventName) => window.removeEventListener(eventName, onEvent));
      document.removeEventListener('visibilitychange', onEvent);
    };
  }, []);

  return null;
};

export default SessionActivityManager;
